import type { EpcProjectMembershipRow, EpcProjectOrgUnit } from '../../../../services/epcProjectService';

export function orgUnitKey(u: EpcProjectOrgUnit): string {
  const r = u as { _id?: string; id?: string };
  return String(r.id ?? r._id ?? '');
}

export type ManagerBranch = {
  manager: EpcProjectMembershipRow;
  engineers: EpcProjectMembershipRow[];
};

export type DepartmentHierarchy = {
  org: EpcProjectOrgUnit;
  orgId: string;
  head?: EpcProjectMembershipRow;
  managers: ManagerBranch[];
};

export function buildDepartmentHierarchies(
  orgUnits: EpcProjectOrgUnit[],
  memberships: EpcProjectMembershipRow[]
): DepartmentHierarchy[] {
  const headsByOrg = new Map<string, EpcProjectMembershipRow>();
  const managersByOrg = new Map<string, EpcProjectMembershipRow[]>();
  for (const m of memberships) {
    if (m.role === 'department_head' && m.orgUnitId) headsByOrg.set(m.orgUnitId, m);
    if (m.role === 'manager' && m.orgUnitId) {
      const arr = managersByOrg.get(m.orgUnitId) ?? [];
      arr.push(m);
      managersByOrg.set(m.orgUnitId, arr);
    }
  }
  const engineersByManager = new Map<string, EpcProjectMembershipRow[]>();
  for (const m of memberships) {
    if (m.role === 'site_engineer' && m.reportsToUserId) {
      const arr = engineersByManager.get(m.reportsToUserId) ?? [];
      arr.push(m);
      engineersByManager.set(m.reportsToUserId, arr);
    }
  }

  return orgUnits.map((org) => {
    const orgId = orgUnitKey(org);
    const head = orgId ? headsByOrg.get(orgId) : undefined;
    const mgrs = orgId ? managersByOrg.get(orgId) ?? [] : [];
    const managers: ManagerBranch[] = mgrs.map((manager) => ({
      manager,
      // Scope engineers to this department — a manager shared across departments
      // must not cause the same engineers to appear in every department they're in.
      engineers: (engineersByManager.get(manager.userId) ?? []).filter(
        (e) => e.orgUnitId === orgId,
      ),
    }));
    return { org, orgId, head, managers };
  });
}

export function initials(name: string | undefined, fallback: string): string {
  const n = (name || '').trim();
  if (!n) return fallback.slice(0, 2).toUpperCase();
  const parts = n.split(/\s+/).filter(Boolean);
  if (parts.length >= 2) {
    return (parts[0]![0]! + parts[parts.length - 1]![0]!).toUpperCase();
  }
  return n.slice(0, 2).toUpperCase();
}
